import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authAPI, systemAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import toast from 'react-hot-toast';
import { HiOutlineMail } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';

function VerifyEmail() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [version, setVersion] = useState('');
  const email = location.state?.email || '';

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await systemAPI.getVersion();
        setVersion(response.data.version);
      } catch (error) {
        console.error('Error fetching version:', error);
        setVersion('1.4.1'); // Fallback
      }
    };
    fetchVersion();

    // Redirect to login if no email
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!code || code.length !== 6) {
      toast.error(t('auth.enterVerificationCode'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.verifyEmail(email, code);

      // Store the token
      localStorage.setItem('token', response.data.access_token);

      toast.success(t('auth.emailVerified'));

      // Redirect to dashboard
      navigate('/dashboard');

      // Reload to trigger auth context
      window.location.reload();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await authAPI.resendVerificationCode(email);
      toast.success(t('auth.codeResent'));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <h1>My Series</h1>
          <p>{t('auth.verifyEmailSubtitle')}</p>
        </div>

        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          <HiOutlineMail
            style={{
              width: '48px',
              height: '48px',
              color: 'var(--primary)',
              margin: '0 auto 16px',
            }}
          />
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            {t('auth.checkYourEmail')}
          </p>
          <p style={{ color: 'var(--text-color)', fontSize: '14px', fontWeight: '500', marginTop: '8px' }}>
            {email}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('auth.verificationCode')}</label>
            <input
              type="text"
              className="form-input"
              placeholder="000000"
              value={code}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only numbers
                if (value.length <= 6) {
                  setCode(value);
                }
              }}
              disabled={loading || resending}
              maxLength={6}
              style={{
                textAlign: 'center',
                fontSize: '24px',
                letterSpacing: '8px',
                fontWeight: '600',
              }}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', marginTop: '8px' }}
            disabled={loading || resending || code.length !== 6}
          >
            {loading ? t('auth.verifying') : t('auth.verify')}
          </button>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={loading || resending}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary)',
                fontSize: '14px',
                cursor: resending ? 'not-allowed' : 'pointer',
                textDecoration: 'underline',
                opacity: resending ? 0.6 : 1,
              }}
            >
              {resending ? t('auth.resendingCode') : t('auth.resendCode')}
            </button>
          </div>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Link
              to="/login"
              style={{
                color: '#6b7280',
                fontSize: '14px',
                textDecoration: 'none',
              }}
            >
              ← {t('auth.backToLogin')}
            </Link>
          </div>
        </form>

        <p
          style={{
            textAlign: 'center',
            marginTop: '24px',
            fontSize: '12px',
            color: '#6b7280',
          }}
        >
          {t('auth.copyright')}
        </p>
        <p
          style={{
            textAlign: 'center',
            marginTop: '8px',
            fontSize: '11px',
            color: '#9ca3af',
          }}
        >
          {t('common.version')} {version}
        </p>
      </div>
    </div>
  );
}

export default VerifyEmail;
